"use client"

/**
 * Custom hook to handle wallet authentication and user session management
 * Manages:
 * - Wallet connection state
 * - User authentication flow
 * - Login method tracking
 * - User info fetching coordination
 */

// React core imports
import { useEffect, useMemo, useCallback, useRef } from "react"

// External libraries and utilities
import { useLogin, usePrivy } from "@privy-io/react-auth"
import localStorage from "@locmod/local-storage"

// Internal application modules
import { type ConnectorName, useWallet } from "wallet"
import { useUserContext } from "src/contexts/user"
import { useUserInfoFetch } from "./useUserInfoFetch"
import { useUtmTracking } from "./useUtmTracking"
import { constants } from "helpers"
import { decryptEmail } from "src/utils/decryptEmail"

export const useWalletAuth = () => {
  // Privy authentication state
  const { user, authenticated } = usePrivy()
  const { setLoginMethod, loginMethod, setLoginCompleted } = useUserContext()
  // UTM and invite code tracking
  const { storedInviteCode, storedUtmParams } = useUtmTracking()

  // UTM email storage keys (same as DgbetaAutoLogin)
  const UTM_EMAIL_KEY = "dgbeta_utm_email"

  // Wallet connection state
  const {
    account,
    isReconnecting,
    isConnecting,
    isConnected,
    isAAWallet,
    connector,
  } = useWallet()

  // Memoize connector name to prevent unnecessary re-renders
  const connectorName = useMemo(
    () => (connector ? { name: connector.name as ConnectorName } : null),
    [connector]
  )

  // Get wallet address based on wallet type
  const walletAddress = useMemo(
    () => (isAAWallet ? user?.smartWallet?.address : user?.wallet?.address),
    [isAAWallet, user?.smartWallet?.address, user?.wallet?.address]
  )

  // User info fetching hook
  const { getUserInfo } = useUserInfoFetch({
    isAAWallet,
    user,
    loginMethod,
    connector: connectorName,
    storedInviteCode,
    storedUtmParams,
  })

  // Store getUserInfo in ref to prevent infinite loops in useEffect
  const getUserInfoRef = useRef(getUserInfo)
  getUserInfoRef.current = getUserInfo

  // Login handler with error handling
  const { login } = useLogin({
    onComplete: useCallback(
      ({ loginMethod }: { loginMethod: string | null }) => {
        try {
          setLoginMethod(loginMethod)
        } catch (error) {
          console.error("Error setting login method:", error)
        }
      },
      [setLoginMethod]
    ),
    onError: useCallback(
      error => {
        console.error("Login error:", error)
        // Reset loading states when login fails
        localStorage.removeItem(constants.localStorageKeys.hasFetchedUserInfo)
        setLoginCompleted(false)
      },
      [setLoginCompleted]
    ),
  })

  // Clear fetch status when disconnected
  useEffect(() => {
    if (!isConnected) {
      localStorage.removeItem(constants.localStorageKeys.hasFetchedUserInfo)
    }
  }, [isConnected])

  // Fetch user info when authenticated
  useEffect(() => {
    if (
      localStorage.getItem(constants.localStorageKeys.hasFetchedUserInfo) !==
        "true" &&
      authenticated &&
      walletAddress
    ) {
      const handleGetUserInfo = async () => {
        try {
          // Use ref to avoid infinite loop
          await getUserInfoRef.current()
          // Only set the flag after successful completion
          localStorage.setItem(
            constants.localStorageKeys.hasFetchedUserInfo,
            "true"
          )
        } catch (error) {
          console.error("Error in getUserInfo:", error)
          // Clear the flag so user can retry
          localStorage.removeItem(constants.localStorageKeys.hasFetchedUserInfo)
        }
      }

      handleGetUserInfo()
    }
  }, [authenticated, walletAddress])

  // Enhanced connectOrCreateWallet with automatic email prefill
  const connectOrCreateWallet = useCallback(
    async (...args: any[]) => {
      if (typeof window !== "undefined") {
        window.localStorage.setItem("manualLoginThisSession", "true")
      }

      // Check if login options are already provided
      const existingOptions = args[0]
      if (existingOptions?.prefill?.value) {
        return login(...args)
      }

      // Check for stored UTM email and decrypt if available
      const storedEncryptedEmail =
        typeof window !== "undefined"
          ? window.localStorage.getItem(UTM_EMAIL_KEY)
          : null

      if (storedEncryptedEmail) {
        try {
          const decryptedEmail = await decryptEmail(storedEncryptedEmail)

          if (decryptedEmail) {
            const loginOptions = {
              ...existingOptions,
              prefill: {
                type: "email" as const,
                value: decryptedEmail,
              },
            }
            return login(loginOptions)
          }
        } catch (error) {
          // Continue without email prefill on decryption failure
        }
      }

      // No stored email or decryption failed, proceed normally
      return login(...args)
    },
    [login]
  )

  return {
    // Authentication state
    authenticated,
    user,
    account,
    walletAddress,
    isAAWallet,
    connectorName,

    // Connection state
    isConnected,
    isConnecting,
    isReconnecting,

    // Login method
    loginMethod,
    connectOrCreateWallet,
    // Actions
    login,
  }
}
